import React, { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import { InferType } from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { societyCreatePostSchema } from '../../../../forms/schemas/society_create_post'
import { FormControl } from '../../../common/FormControl'
import { TextArea } from '../../../common/TextArea'
import { AttachmentsContainer } from '../../AttachmentsContainer'
import { RoleDropdown } from '../../RoleDropdown'
import { SocietyDropdown } from '../../SocietyDropdown'
import { VisibilityDropdown } from '../../VisibilityDropdown'
import { useUserFeedSocieties } from '../../../../views/feed/useUserFeedSocieties'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { useFormErrorMessage } from '../../../../hooks/useFormErrorMessage'
import { Button, ButtonVariant } from '../../../common/Button'
import {
  isUserAdminInSociety,
  isUserBoardMemberInSociety,
} from '../../../../helpers/society'
import { useMessageTextAreaRows } from './useMessageTextAreaRows'
import { useModal } from '../../../../hooks/useModal'
import { PreviewPostModal } from './PreviewPostModal'
import { useSocietyLockedForCurrentUser } from '../../../../hooks/useSocietyLockedForCurrentUser'
import { Callout } from '../../../society/Callout'
import { reverseUrl } from '../../../../navigation/reverseUrl'
import { useUserBlockedInSociety } from '../../../../hooks/useUserBlockedInSociety'
import { BlockedCallout } from '../../../blocked/BlockedCallout'
import { IPostModel } from '../../../../interfaces/models/posts.interfaces'
import { groupQueries } from '../../../../api/groups'
import { userQueries } from '../../../../api/users'
import { useAuth } from '../../../../hooks/useAuth'
import { Toggle } from '../../../common/Toggle'
import { Label } from '../../../common/Label'
import { i18nSocietyType } from '../../../../i18n'
import {
  PostAttachmentFiles,
  PostAttachments,
} from '../../AttachmentsContainer/attachmentSeed'
import { NullableDateTimeInput } from '../../../common/DateTimeInput'
import { theme } from '../../../../theme/theme'
import { PostRow } from '../../../../generated/api/post-row'

export type PostEditSeed = PostAttachments &
  Pick<PostRow, 'isScheduled' | 'createdAt'>

interface CreateEditPostModalContentProps {
  post?: IPostModel
  attachments?: PostEditSeed
  onError: (error: unknown) => void
  onSubmit: (data: InferType<typeof societyCreatePostSchema>) => Promise<void>
  onClose: () => void
  loading: boolean
}

export const CreateEditPostModalContent = ({
  post,
  attachments,
  onError,
  onSubmit,
  onClose,
  loading,
}: CreateEditPostModalContentProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { getErrorMessage } = useFormErrorMessage()
  const { userId: authUserId } = useAuth()
  const userId = authUserId as string
  const { societies } = useUserFeedSocieties({ userId })
  const messageTextAreaRows = useMessageTextAreaRows()
  const [loadingAttachment, setLoadingAttachment] = useState(false)
  const [attachmentFiles, setAttachmentFiles] = useState<PostAttachmentFiles>({
    media: [],
    documents: [],
  })
  const { data: user } = useQuery(userQueries.getUser(userId))

  const {
    show: showPreviewPostModal,
    open: openPreviewPostModal,
    close: closePreviewPostModal,
  } = useModal()

  const scheduledAt = attachments?.isScheduled
    ? new Date(attachments.createdAt)
    : null

  const getDefaultValues = {
    message: post?.message || '',
    author: post?.author || userId,
    role: post?.role || 'resident',
    society: post?.society || '',
    isAlert: post?.isAlert || false,
    pollIds: attachments?.polls.map((poll) => poll.id) ?? post?.pollIds ?? [],
    eventsIds: post?.eventsIds || [],
    groupsIds: post?.groupsIds || [],
    media: post?.media || [],
    documentsIds: post?.documentsIds || [],
    accessBoardMember: post?.accessBoardMember || false,
    accessAdminMember: post?.accessAdminMember || false,
    accessUsersIds: post?.accessUsersIds || [],
    isCommentDisabled: post?.isCommentDisabled || false,
    publishAt: scheduledAt,
    pinned: !!post?.pinnedAt,
  }

  const {
    control,
    watch,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isValid },
  } = useForm<InferType<typeof societyCreatePostSchema>>({
    mode: 'all',
    resolver: yupResolver(societyCreatePostSchema),
    defaultValues: getDefaultValues,
  })

  const watchSociety = watch('society')

  useQuery(groupQueries.getGroupsPerSociety(watchSociety))

  const watchRole = watch('role')
  const watchPollIds = watch('pollIds')
  const watchEventsIds = watch('eventsIds')
  const watchGroupsIds = watch('groupsIds')
  const watchAccessBoardMember = watch('accessBoardMember')
  const watchAccessAdminMember = watch('accessAdminMember')
  const watchIsAlert = watch('isAlert')
  const watchPinned = watch('pinned')

  // effect to handle init values and custom changes to role
  useEffect(() => {
    const postSociety = societies.find(
      (society) => society._id === watchSociety
    )
    const isBoardMemberOfSelectedSociety = isUserBoardMemberInSociety(
      postSociety,
      userId
    )
    const isAdminOfSelectedSociety = isUserAdminInSociety(postSociety, userId)
    const firstSociety = societies[0]
    if (!post?.society && watchSociety === '' && firstSociety) {
      setValue('society', firstSociety?._id)
    }
    if (post?.role) {
      return
    }
    if (
      postSociety?.residentPostingDisabledByAdmin &&
      isAdminOfSelectedSociety
    ) {
      setValue('role', 'admin')
    } else if (
      postSociety?.residentPostingDisabledByAdmin &&
      isBoardMemberOfSelectedSociety
    ) {
      setValue('role', 'board-member')
    } else {
      setValue('role', 'resident')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, watchSociety])

  useEffect(() => {
    if (watchRole === 'resident') {
      setValue('groupsIds', [])
      setValue('isAlert', false)
      setValue('publishAt', null)
      setValue('pinned', false)
    }
  }, [watchRole, setValue])

  const watchSocietyModel = societies.find(
    (society) => society._id === watchSociety
  )
  const selectedSocietyLockedForUser =
    useSocietyLockedForCurrentUser(watchSocietyModel)
  const userBlockedInSelectedSociety = useUserBlockedInSociety(watchSociety)

  const watchPublishAt = watch('publishAt')
  const canPin =
    watchRole !== 'resident' &&
    (isUserAdminInSociety(watchSocietyModel, userId) ||
      isUserBoardMemberInSociety(watchSocietyModel, userId))
  const canSchedule = canPin && (!post || !!scheduledAt)
  const minPublishAt = new Date()
  const maxPublishAt = new Date()
  maxPublishAt.setFullYear(maxPublishAt.getFullYear() + 1)

  let submitLabel = translate('feedView.create')
  if (post) {
    submitLabel = translate('common.actions.save')
  } else if (watchPublishAt) {
    submitLabel = translate('scheduledPosts.form.scheduleAction')
  }

  return (
    <>
      <form
        className="flex h-full flex-col"
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <div className="flex h-full flex-col gap-6 overflow-y-auto px-6 pb-6 md:pb-8">
          <div className="flex flex-col flex-wrap gap-4 md:flex-row">
            {!post?.society && (
              <SocietyDropdown
                societies={societies}
                setValue={setValue}
                control={control}
              />
            )}
            <Controller
              control={control}
              name="role"
              render={({ field: { value, onChange } }) => (
                <RoleDropdown
                  value={value}
                  onChange={onChange}
                  watchSociety={watchSociety}
                  userId={userId}
                  excludeResident={!!scheduledAt}
                />
              )}
            />
            <VisibilityDropdown
              setValue={setValue}
              control={control}
              watchRole={watchRole}
              watchSociety={watchSociety}
              watchGroupsIds={watchGroupsIds}
              watchAccessAdminMember={watchAccessAdminMember}
              watchAccessBoardMember={watchAccessBoardMember}
            />
          </div>
          {selectedSocietyLockedForUser && (
            <div>
              <Callout
                text={translate('societySideBar.callouts.societyLocked.title', {
                  context: i18nSocietyType(
                    watchSocietyModel?.societyTypeDisplayName
                  ),
                })}
                path={reverseUrl('management:billing-payment', {
                  id: watchSociety,
                })}
                warning
              />
            </div>
          )}
          {userBlockedInSelectedSociety && (
            <BlockedCallout selectedSocietyId={watchSociety} />
          )}
          <div className="flex flex-col gap-3">
            <Controller
              control={control}
              name="message"
              render={({ field: { value, name, onChange, onBlur } }) => (
                <FormControl
                  label={translate('common.form.labels.message')}
                  name={name}
                  error={errors.message && getErrorMessage(errors.message)}
                >
                  <TextArea
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    error={!!errors.message}
                    fixedHeight
                    rows={messageTextAreaRows}
                  />
                </FormControl>
              )}
            />
            <div className="flex flex-row gap-2 justify-between">
              <AttachmentsContainer
                control={control}
                setValue={setValue}
                setLoadingAttachment={setLoadingAttachment}
                watchSociety={watchSociety}
                watchEventsIds={watchEventsIds}
                watchRole={watchRole}
                watchPollIds={watchPollIds}
                watchIsAlert={watchIsAlert}
                canPin={canPin}
                watchPinned={watchPinned}
                post={post}
                attachments={attachments}
                onFilesChange={setAttachmentFiles}
              />
            </div>
            {watch('role') !== 'resident' && (
              <Controller
                control={control}
                name="isCommentDisabled"
                render={({ field: { onChange, value, name } }) => (
                  <div className="flex gap-2 items-center">
                    <Label
                      name={name}
                      label={translate(
                        'createUpdateEvent.form.labels.allowComments'
                      )}
                    />
                    <Toggle
                      name={name}
                      enabled={!value}
                      onChange={(e) => {
                        onChange(!e)
                      }}
                    />
                  </div>
                )}
              />
            )}
            {canSchedule && (
              <Controller
                control={control}
                name="publishAt"
                render={({ field: { value, onChange, name } }) => (
                  <FormControl
                    label={translate('scheduledPosts.form.label')}
                    name={name}
                    error={
                      errors.publishAt && getErrorMessage(errors.publishAt)
                    }
                  >
                    <div className="flex flex-col gap-1 md:max-w-96">
                      <NullableDateTimeInput
                        value={value ? new Date(value) : null}
                        onChange={onChange}
                        rawDates
                        timeColumns
                        showWeekNumbers={false}
                        minDate={minPublishAt}
                        maxDate={maxPublishAt}
                        dateFormat="eee d MMMM yyyy 'kl.' HH:mm"
                      />
                      <p
                        className="text-neutral-40"
                        style={theme.textVariants.fineprint}
                      >
                        {translate('scheduledPosts.form.helpText')}
                      </p>
                    </div>
                  </FormControl>
                )}
              />
            )}
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-4 px-6 py-6 md:px-12 md:py-8">
          <Button
            label={translate('common.actions.cancel')}
            onClick={onClose}
          />
          <Button
            label={translate('previewPost.preview')}
            onClick={openPreviewPostModal}
            variant={ButtonVariant.SECONDARY}
            disabled={!user || !watchSocietyModel}
          />
          <Button
            variant={ButtonVariant.PRIMARY}
            loading={loading}
            disabled={
              userBlockedInSelectedSociety ||
              selectedSocietyLockedForUser ||
              !isValid ||
              loading ||
              loadingAttachment
            }
            label={submitLabel}
            type="submit"
          />
        </div>
      </form>
      {user && watchSocietyModel && (
        <PreviewPostModal
          society={watchSocietyModel}
          show={showPreviewPostModal}
          close={closePreviewPostModal}
          createPostData={getValues()}
          files={attachmentFiles}
        />
      )}
    </>
  )
}
